import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { useCallback, useMemo } from "react";
import log from "logbeacon";
import { getDeviceParser, getDeviceType, pubsub } from "utils";
import { TaskListResponse } from "../features/api/QuestsApi";
// import { TaskListResponse } from "../plugin/api/QuestsApi";
import { UserStore } from "../store";
import { QUERY_KEYS } from "../config";
import { pubSubKeyMap } from "../utils/pubSubKeyMap";
import { sleep } from "../utils/sleep";
import { REMOTE_CONFIG_KEY_IS_NOTIFICATION_TASK_ENABLED } from "../features/firebase/remoteConfigKeys";
import { useOspApiClientProvider } from "./useOspApiClientProvider";
import { getValue } from "@/src/features/firebase/initFirebase";

export type PostTaskResponse = TaskListResponse & {
  type: string;
};

const isNotificationTaskEnabled = async () => {
  try {
    const configValue = (
      await getValue(REMOTE_CONFIG_KEY_IS_NOTIFICATION_TASK_ENABLED)
    ).asString();
    const config = JSON.parse(configValue);
    const platform = getDeviceType();
    const buildNumber = config[platform];
    const deviceInfo = getDeviceParser();
    log.debug(
      "[usePostTaskList] buildNumber",
      buildNumber,
      deviceInfo,
      config,
      platform,
    );
    return Number(deviceInfo.buildNumber) > buildNumber;
  } catch (error) {
    return false;
  }
};

export const usePostTaskList = (params?: {
  position: string;
  channel?: string;
  locale?: string;
}) => {
  const { questApi } = useOspApiClientProvider();
  const { ownerInfo } = useSnapshot(UserStore);
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => [
      QUERY_KEYS.CREDENTIAL_POST_TASK_LIST,
      ownerInfo?.id,
      ...Object.values(params || {}),
    ],
    [ownerInfo?.id, params],
  );

  const { data, ...reset } = useQuery({
    queryKey,
    enabled: !!questApi && !!ownerInfo?.id,
    // todo perf
    staleTime: 1000 * 60 * 1,
    queryFn: async () => {
      // 真实 API 调用
      const response = await questApi?.v4.tasksListList(params);
      if (!response?.data?.obj) {
        return [];
      }
      const tasks = response?.data?.obj;
      const shouldShowNotificationTask = await isNotificationTaskEnabled();
      if (!shouldShowNotificationTask) {
        const index = tasks.findIndex(
          (task) => task.code === "onboarding_missions",
        );
        if (index !== -1 && tasks[index]) {
          tasks[index].subTask = tasks[index].subTask?.filter(
            (subTask) => subTask.code !== "open_notification",
          );
        }
      }
      log.debug("[usePostTaskList] queryFn", tasks, shouldShowNotificationTask);
      // 根据任务完成状态排序 status 0 未完成 1 已完成 未完成的放在前面
      return (
        tasks
          // .filter((task) => !(task.cycle === "once" && task.status === 1))
          .sort((a, b) => (a.status ?? 0) - (b.status ?? 0))
      );
    },
  });

  // 更新缓存的任务数据（支持更新顶层 task 和 subTask）
  const updateTaskList = useCallback(
    (taskId: string, task: Partial<PostTaskResponse>) => {
      if (!data) {
        return;
      }

      // 1. 先尝试在顶层 task 中查找
      const topIndex = data.findIndex((item) => item.taskId === taskId);

      if (topIndex !== -1) {
        // 找到顶层 task，直接更新
        queryClient.setQueryData(queryKey, (oldData: any) => {
          if (!oldData) {
            return [];
          }
          const newData = oldData.map((item: PostTaskResponse) => {
            if (item.taskId === taskId) {
              return {
                ...item,
                ...task,
              };
            }
            return item;
          });
          return newData;
        });
        return;
      }

      // 2. 顶层找不到，遍历所有 task 的 subTask 查找
      for (let i = 0; i < data.length; i++) {
        const parentTask = data[i];
        if (!parentTask.subTask) {
          continue;
        }

        const subIndex = parentTask.subTask.findIndex(
          (sub) => sub.taskId === taskId,
        );

        if (subIndex !== -1) {
          // 找到对应的 subTask，更新它
          queryClient.setQueryData(queryKey, (oldData: any) => {
            if (!oldData) {
              return [];
            }
            const newData = oldData.map((item: PostTaskResponse) => {
              if (item.taskId === parentTask.taskId && item.subTask) {
                // 更新 subTask
                const updatedSubTasks = item.subTask.map((sub: any) =>
                  sub.taskId === taskId ? { ...sub, ...task } : sub,
                );

                return {
                  ...item,
                  subTask: updatedSubTasks,
                };
              }
              return item;
            });
            return newData;
          });
          return;
        }
      }
    },
    [queryClient, queryKey, data],
  );

  const tasksVerifyCreate = useCallback(
    async (params: { taskId: string; wallet_address?: string }) => {
      const { taskId, wallet_address } = params;
      if (!questApi) {
        return;
      }
      const ext = wallet_address
        ? { ext: JSON.stringify({ wallet_address }) }
        : {};
      const response = await questApi.v4.tasksVerifyCreate({
        taskId,
        ...ext,
      });
      return response?.data;
    },
    [questApi],
  );

  const checkIn = useCallback(async () => {
    if (!questApi) {
      return;
    }
    const response = await questApi.v4.checkIn();
    await sleep(1000);
    pubsub.emit(pubSubKeyMap.TASK_COMPLETED_CREDITS);
    reset.refetch();
    return response?.data;
  }, [questApi, reset]);

  return {
    postTaskList: data as PostTaskResponse[],
    updateTaskList,
    tasksVerifyCreate,
    checkIn,
    ...reset,
  };
};
